import { useState, useEffect, useMemo, useCallback } from "react";
import { getDevelopers, getStages } from "../../CachedData";
import { DEFAULT_FILTERS, FILTER_TYPES } from "./constants";
import { getWorkflowStages, getNodesForStage, getNodesForStages } from "./workflowUtils";
import { buildFilterChipData } from "./filterUtils";

const FILTER_PATCH_BUILDERS = {
  [FILTER_TYPES.STAGE]: (value) => ({ stageIds: value.stageIds, nodeIds: value.nodeIds ?? [] }),
  [FILTER_TYPES.ASSIGNED_TO]: (value) => ({ assignedTo: value }),
  [FILTER_TYPES.CREATED_DATE]: (value) => ({ createdDate: value }),
  [FILTER_TYPES.UPDATED_DATE]: (value) => ({ updatedDate: value }),
  [FILTER_TYPES.STAGE_UPDATED]: (value) => ({ stageUpdated: value }),
  [FILTER_TYPES.CUSTOM]: (value, previous) => ({ customFields: { ...(previous.customFields || {}), [value.key]: value.condition } }),
};

const CHIP_TYPE_TO_RESET_FIELD = {
  createdDate: "createdDate",
  updatedDate: "updatedDate",
  stageUpdated: "stageUpdated",
};

export function useLeadFilters(filters, setFilters, search, setSearch, activeFilterCount, workspaceId, pipelineId, customFields = []) {
  const [workflowStages, setWorkflowStages] = useState([]);
  const [users, setUsers] = useState([]);

  const fetchWorkflowStages = useCallback(async () => {
    try {
      const data = await getStages(pipelineId);
      setWorkflowStages(Array.isArray(data) ? data : data?.data || []);
    } catch (error) {
      console.error("Failed to fetch workflow stages:", error);
      setWorkflowStages([]);
    }
  }, [pipelineId]);

  const fetchUsers = useCallback(async () => {
    try {
      const data = await getDevelopers(workspaceId);
      setUsers(Array.isArray(data) ? data : data?.data || []);
    } catch (error) {
      console.error("Failed to fetch users:", error);
      setUsers([]);
    }
  }, [workspaceId]);

  useEffect(() => {
    fetchWorkflowStages();
    fetchUsers();
  }, [fetchWorkflowStages, fetchUsers]);

  const stages = useMemo(() => getWorkflowStages(workflowStages), [workflowStages]);

  const selectedStage = useMemo(
    () => stages.find((stage) => stage.id === filters.stageId) || null,
    [stages, filters.stageId]
  );

  const nodesForStage = useMemo(() => getNodesForStage(workflowStages, filters.stageId), [workflowStages, filters.stageId]);

  const activeFilterChips = useMemo(
    () => buildFilterChipData(filters, { stages, users, workflowStages, customFields }),
    [customFields, filters, stages, workflowStages, users]
  );


  const applyFilter = useCallback((filterType, value) => {
    const buildPatch = FILTER_PATCH_BUILDERS[filterType];
    if (!buildPatch) return;

    setFilters((previousFilters) => ({ ...previousFilters, ...buildPatch(value, previousFilters) }));
  }, [setFilters]);

  const removeFilterChip = useCallback(
    (chip) => {
      if (chip.filterType === "stage") {
        setFilters((prev) => {
          const nodeIdsInStage = getNodesForStages(workflowStages, [chip.stageId]).map((node) => node.id);
          return {
            ...prev,
            stageIds: prev.stageIds.filter((id) => id !== chip.stageId),
            nodeIds: prev.nodeIds.filter((id) => !nodeIdsInStage.includes(id)),
          };
        });
        return;
      }

      if (chip.filterType === "assignedTo") {
        setFilters((prev) => ({
          ...prev,
          assignedTo: prev.assignedTo.filter((id) => id !== chip.userId),
        }));
        return;
      }

      if (chip.filterType === "queue") {
        setFilters((prev) => ({
          ...prev,
          leadIds: [],
          queueKey: null,
          queueLabel: null,
        }));
        return;
      }

      if (chip.filterType === "custom") {
        setFilters((prev) => {
          const nextCustomFields = { ...(prev.customFields || {}) };
          delete nextCustomFields[chip.fieldKey];
          return { ...prev, customFields: nextCustomFields };
        });
        return;
      }

      const resetField = CHIP_TYPE_TO_RESET_FIELD[chip.filterType];
      if (resetField) {
        setFilters((prev) => ({ ...prev, [resetField]: null }));
      }
    },
    [workflowStages, setFilters]
  );

  const resetFilters = useCallback(() => {
    setSearch("");
    setFilters(DEFAULT_FILTERS);
  }, [setFilters, setSearch]);

  return {
    // cached reference data
    workflowStages,
    users,
    stages,
    selectedStage,
    nodesForStage,

    // search
    search,
    setSearch,

    // filters
    filters,
    setFilters,
    applyFilter,
    removeFilterChip,
    resetFilters,
    activeFilterChips,
    activeFilterCount,
  };
}
